import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { selectScale, selectIsBtnExportImageEnabled } from '../../redux/diagram/diagram.selectors';
import { selectDiagramPrefs, resolveInputMode } from '../../redux/preferences/preferences.selectors';
import { hideNoteLinkPreviewImmediately } from '../NoteLinkPreview/noteLinkHoverStore';

import {
    useCanvasScrollGeometry,
    applyCanvasScrollPan,
    endCanvasScrollPanGesture,
    getCanvasScrollPanToken,
} from './canvasScrollbarsStore';
import {
    computeAxisRange,
    computeAxisScrollModel,
    computeThumbGeometryPx,
    thumbDeltaPxToOriginDelta,
    trackClickToOrigin,
} from './scrollbarMath';

import './CanvasScrollbars.scss';

// Fraction of the current viewport's world width/height added on each side —
// lets the user always scroll a bit past the outermost element instead of
// the thumb slamming into the end of the track exactly at the content edge.
// User-tuned: started at 0.25, halved on request ("make this gap smaller").
const PAD_FRACTION = 0.25;

// Fixed corner gap reserved so the two bars never overlap where they'd
// meet at the bottom-right, regardless of which one is currently visible —
// avoids a layout feedback loop from measuring the other bar's DOM size.
const CORNER_GAP_PX = 14;

const axisInputs = (contentBounds, viewport, minKey, maxKey) => {
    const viewportMin = viewport[minKey];
    const viewportMax = viewport[maxKey];
    const pad = (viewportMax - viewportMin) * PAD_FRACTION;
    return {
        contentMin: contentBounds ? contentBounds[minKey] : null,
        contentMax: contentBounds ? contentBounds[maxKey] : null,
        viewportMin,
        viewportMax,
        pad,
    };
};

// One bar (horizontal OR vertical) — everything below is written generic
// over `axis` ('x' | 'y') so the two bars are exactly one code path.
const ScrollbarAxis = ({ axis, trackLengthPx, inputs, scale, trackStyle }) => {
    // Two hooks in this component, both called unconditionally on every
    // render regardless of the visibility branch below — everything else
    // here is plain values/functions, not hooks, so there's no
    // rules-of-hooks concern in returning null further down.
    const trackRef = useRef(null);

    // CRITICAL FIX (review): the active drag's document listeners, tracked
    // so they can be torn down from EITHER the gesture's own mouseup OR
    // this component's unmount cleanup below — previously removal only
    // happened in mouseup, so a mid-drag unmount (leaving the diagram
    // editor while still holding the thumb down) left `document` listeners
    // attached forever, applying stray mousemove deltas to whatever
    // Canvas — even an unrelated diagram's — happens to be registered by
    // the time the next event fires. null when no drag is in flight.
    const activeDragRef = useRef(null); // null | { handleMouseMove, handleMouseUp }

    useEffect(() => () => {
        const active = activeDragRef.current;
        if (!active) return;
        document.removeEventListener('mousemove', active.handleMouseMove);
        document.removeEventListener('mouseup', active.handleMouseUp);
        activeDragRef.current = null;
        // Best-effort: if the token this drag started with still matches a
        // live Canvas, clear its flags immediately rather than leaving them
        // to the 200ms timer. A no-op if Canvas itself already unmounted
        // (token mismatch) — see canvasScrollbarsStore.js.
        endCanvasScrollPanGesture(active.token);
    }, []);

    const model = computeAxisScrollModel(inputs);
    if (!model.visible) return null;

    const { lengthPx, posPx, maxPosPx } = computeThumbGeometryPx({
        thumbFrac: model.thumbFrac,
        scrollFrac: model.scrollFrac,
        trackLengthPx,
    });

    // Thumb drag: one continuous mousedown -> document mousemove -> document
    // mouseup gesture, same shape as the canvas's own mouse-drag-pan (not
    // the bursty trackpad wheel-pan), so Canvas's registered handler applies
    // isBoardMoving flags for it exactly like that drag path does — see the
    // registration site in Canvas.jsx for the full justification. Plain
    // function (not useCallback) — `inputs` is a fresh object every render
    // already, so memoizing here wouldn't skip any renders.
    const handleThumbMouseDown = (event) => {
        event.preventDefault();
        event.stopPropagation(); // don't also trigger the track's jump-on-click below
        hideNoteLinkPreviewImmediately();

        // Captured ONCE for the whole gesture (review CRITICAL 1): identifies
        // which Canvas instance was live when this drag started. Every
        // subsequent call below hands this back — if a different Canvas
        // instance has since registered (a diagram switch mid-drag), the
        // store drops the call instead of applying it to the wrong diagram.
        const token = getCanvasScrollPanToken();
        const { rangeMin, rangeMax } = computeAxisRange(inputs);
        let lastClientPos = axis === 'x' ? event.clientX : event.clientY;

        const handleMouseMove = (moveEvent) => {
            const clientPos = axis === 'x' ? moveEvent.clientX : moveEvent.clientY;
            const deltaPx = clientPos - lastClientPos;
            lastClientPos = clientPos;
            if (!deltaPx) return;
            const deltaOrigin = thumbDeltaPxToOriginDelta({
                deltaPx,
                rangeMin,
                rangeMax,
                viewportMin: inputs.viewportMin,
                viewportMax: inputs.viewportMax,
                maxPosPx,
                scale,
            });
            applyCanvasScrollPan(axis, deltaOrigin, token);
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            activeDragRef.current = null;
            // IMPORTANT 4 (review): clear isBoardMoving/isBoardMovingStarted
            // immediately on release instead of leaving it to the per-tick
            // applyDelta's 200ms inactivity timer — matches the immediate
            // clear the native board-drag path gets on ITS mouseup.
            endCanvasScrollPanGesture(token);
        };

        activeDragRef.current = { handleMouseMove, handleMouseUp, token };
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    // Track click (not on the thumb — the thumb's own handler stops
    // propagation above, and this guard double-checks via event.target):
    // jump so the thumb centers on the click, one shot.
    const handleTrackMouseDown = (event) => {
        if (event.target !== trackRef.current) return;
        hideNoteLinkPreviewImmediately();

        const token = getCanvasScrollPanToken();
        const trackRect = trackRef.current.getBoundingClientRect();
        const clickPosPx = axis === 'x' ? event.clientX - trackRect.left : event.clientY - trackRect.top;

        const { rangeMin, rangeMax } = computeAxisRange(inputs);
        const currentOrigin = -inputs.viewportMin * scale;
        const targetOrigin = trackClickToOrigin({
            clickPosPx,
            trackLengthPx,
            thumbLengthPx: lengthPx,
            rangeMin,
            rangeMax,
            viewportMin: inputs.viewportMin,
            viewportMax: inputs.viewportMax,
            scale,
        });
        applyCanvasScrollPan(axis, targetOrigin - currentOrigin, token);
        // One-shot gesture (no drag follow-through) — clear immediately
        // rather than leaving isBoardMoving flapping on for 200ms after a
        // single click, same reasoning as the drag mouseup above.
        endCanvasScrollPanGesture(token);
    };

    const thumbStyle = axis === 'x'
        ? { left: `${posPx}px`, width: `${lengthPx}px` }
        : { top: `${posPx}px`, height: `${lengthPx}px` };

    return (
        <div
            ref={trackRef}
            className={`canvas-scrollbar canvas-scrollbar--${axis}`}
            style={trackStyle}
            onMouseDown={handleTrackMouseDown}
        >
            <div
                className="canvas-scrollbar__thumb"
                style={thumbStyle}
                onMouseDown={handleThumbMouseDown}
            />
        </div>
    );
};

// Horizontal bar along the canvas bottom + vertical bar along the right
// edge, reflecting and controlling the pan position. Mounted once at the
// Diagram.jsx level, same tier as DiagramSearch/NoteLinkPreview — reads
// geometry Canvas publishes to canvasScrollbarsStore and pans the canvas
// back through that same store's imperative API (see there for why: this
// component is Canvas's sibling, not its child, and can outlive any one
// Canvas instance across a diagram switch).
//
// No ARIA/keyboard affordances on the thumbs — a deliberate omission
// consistent with the rest of the canvas's interaction model (the `<canvas>`
// surface itself has none either; keyboard-driven canvas navigation is out
// of scope for this feature).
const CanvasScrollbars = () => {
    const scale = useSelector(selectScale);
    const isBtnExportImageEnabled = useSelector(selectIsBtnExportImageEnabled);
    const diagramPrefs = useSelector(selectDiagramPrefs);
    const geometry = useCanvasScrollGeometry();

    // Scrollbars are a trackpad-mode affordance (user decision): in mouse
    // mode the wheel already pans/zooms the canvas directly, so the bars
    // are hidden entirely. The explicit settings toggle wins on top of that.
    // 'auto' resolves through resolveInputMode (currently → 'trackpad').
    if (resolveInputMode(diagramPrefs?.input_mode) !== 'trackpad') return null;
    if (!diagramPrefs?.show_scrollbars) return null;

    // Export-image mode has its own selection-box overlay covering the same
    // screen area (Minimap is hidden the same way in Diagram.jsx) — stay out
    // of its way rather than layering a scrollbar on top of it.
    if (isBtnExportImageEnabled) return null;
    if (!geometry) return null;

    const { contentBounds, viewport } = geometry;
    const trackLengthX = Math.max(0, window.innerWidth - CORNER_GAP_PX);
    const trackLengthY = Math.max(0, window.innerHeight - CORNER_GAP_PX);

    return (
        <>
            <ScrollbarAxis
                axis="x"
                trackLengthPx={trackLengthX}
                inputs={axisInputs(contentBounds, viewport, 'minX', 'maxX')}
                scale={scale}
                trackStyle={{ right: `${CORNER_GAP_PX}px` }}
            />
            <ScrollbarAxis
                axis="y"
                trackLengthPx={trackLengthY}
                inputs={axisInputs(contentBounds, viewport, 'minY', 'maxY')}
                scale={scale}
                trackStyle={{ bottom: `${CORNER_GAP_PX}px` }}
            />
        </>
    );
};

export default CanvasScrollbars;
